/**
 * @summary - Hook for handling Firebase notifications
 * @author Vengateshwaran K
 * @since 2024-06-21
 */
import React, { useState, useEffect } from 'react';
import { onMessage } from 'firebase/messaging';
import { messaging } from './firebaseConfig';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom'; // For navigation
import { BemsGraph, FemsGraph } from "./utility/apiURLS";
import { getAuthToken, getFacilityId } from "./utility/tokenUtility";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  updateOutsourceDetails,
  updateWorkOrderStatus,
} from "../src/redux/actions/workOrderActionTypes";
// Path to notification sound
const notificationSound = '/mixkit-software-interface-remove-2576.WAV';

// Set to track displayed notifications by unique identifier (e.g., title or custom ID)
const displayedNotifications = new Set();

const useNotificationHandler = (addNotification) => {
  const facilityid = getFacilityId("");
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Navigation hook
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth()+1;
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState([]);
  const token = getAuthToken("id_token");
  const roleName = localStorage.getItem('roleName')?.trim();  // Fetch roleName once and trim spaces
  const userid = localStorage.getItem("UserId");
  const departmentid = localStorage.getItem("roleName") === "DeptUser" ? 1:0;
  const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  // Function to handle notification click
  const handleNotificationClick = async(data) => {
    let response;
    if(roleName === 'Helpdesk' || roleName === 'FMSExec'){
    if (data?.WorkGroupType === "FEMS") {
        response = await axios.get(
          `${FemsGraph}/api/GraphfemDepartmentrequest/GetWorkRequestExport/${facilityid}/${currentYear}/${currentMonth}?filterOptions=${JSON.stringify(
            filterModel
          )}&sortOptions=${JSON.stringify(sortModel)}`,
          config
        );

      } else if (data?.WorkGroupType === "BEMS") {
        response = await axios.get(
          `${BemsGraph}/api/GraphbemDepartmentrequest/GetWorkRequestExport/${facilityid}/${currentYear}/${currentMonth}?filterOptions=${JSON.stringify(
            filterModel
          )}&sortOptions=${JSON.stringify(sortModel)}`,
          config
        );
    }
    response=response?.data?.filter((item)=>item.id===data?.WorkOrderId);
    if(response?.length>0){      
    navigate("departmentworkrequest/register", {
          state: {
            ...response[0],
            isEditMode: true,
            assetno: { label: response[0].assetno, value: response[0].assetId },
            categoryId: { label: response[0].categoryName, value: response[0].categoryId },
            typeId: { label: response[0].type, value: response[0].typeId },
          },
        });
    }
  }else if(roleName === 'FMSExec' || roleName === 'HospitalFM'){
    const [year, month] = data?.Date?.split("-").map(Number);
    if (data?.WorkGroupType === "FEMS") {
      response = await axios.get(
        `${FemsGraph}/api/Graphfemworkorder/GetCombinedWorkOrderSchedules/${facilityid}/${year}/${month}/${departmentid}/${userid}/${token}`,
        config
    )
    } else if (data?.WorkGroupType === "BEMS") {
      response = await axios.get(
        `${BemsGraph}/api/Graphbemworkorder/GetCombinedWorkOrderSchedules/${facilityid}/${year}/${month}/${departmentid}/${userid}/${token}`,
        config
    )
  }
  response=response?.data?.items?.filter((item)=>item.id===data?.WorkOrderId);
  if(response?.length>0){      
    const serviceType = response?.service === "FEMS" ? "fem" : "bem";
    const categoryType = response?.maintenanceType === "Scheduled" ? "Scheduled" : "Unscheduled";
    const title = `Edit ${categoryType} Work Order`;
         navigate("workorder/register", { state: { ...response, service: serviceType, readOnly: false, editmode: true, category: categoryType, viewmode: false, title } });

    }

  }
};


  useEffect(() => {
    const requestNotificationPermission = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          // console.log("Notification permission granted.");
        } else {
          console.warn("Notification permission denied.");
        }
      } catch (error) {
        console.error("Error requesting notification permission:", error);
      }
    };

    // Request permission for notifications (if not already granted)
    if (Notification.permission !== "granted") {
      requestNotificationPermission();
    }

    // Listen for messages when the app is in the foreground
    const unsubscribe = onMessage(messaging, (payload) => {
      const title = payload.notification?.title || "New Notification";
      const body = payload.notification?.body?.trim() || "You have a new message.";
      const notificationId = `${title}-${body}`; // Create a unique identifier for the notification
      const timestamp = new Date().toISOString(); // Add timestamp for sorting
      const data = payload?.data || "";

      // Create a notification object
      const notification = {
        id: `${title}-${timestamp}`, // Unique identifier for each notification
        title,
        body,
        timestamp,
        data,
      };

      // Push notification to state
      addNotification(notification);

      // Check if this notification is already displayed
      if (displayedNotifications.has(notificationId)) {
        // console.log("Duplicate notification skipped:", notificationId);
        return; // Skip displaying duplicate notifications
      }

      // Add notification ID to the set
      displayedNotifications.add(notificationId);

      // Play notification sound
      const audio = new Audio(notificationSound);
      audio.play();

      // Show a Toast notification in the app as well
      toast.info(`${title}: ${body}`, {
        position: "top-right",
        autoClose: 20000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: {
          fontWeight:'bold',
          fontSize: "13px",
          padding: "5px",
          width: "90%",
          maxWidth: "900px",
          maxHeight: "300px",
          whiteSpace: "pre-wrap",
          overflowWrap: "break-word",
        },
        onClose: () => {
          // Remove the notification ID from the set after it's closed
          displayedNotifications.delete(notificationId);
        },
        onClick: () => handleNotificationClick(data), // Call the separate handler
      });
    });

    // Cleanup function
    return () => {
      unsubscribe();
    };
  }, [addNotification, navigate]);
};

export default useNotificationHandler;
